import { Friends } from "./Friends.js";
import { gameError } from "../net/game-errors.js";
import { MobileLobbyView, WideLobbyView } from "../features/lobby/LobbyViews.js";
import { useLobby } from "../features/lobby/useLobby.js";
import { useEffect, useState } from "react";
import { Gift, Mail, Trophy, Coins as CoinsIcon, DoorOpen, Users, ArrowUpRight, Sparkles, Accessibility, ChevronRight, Clipboard, Globe2, ShieldCheck, Volume2 } from "lucide-react";
import { useLocation, useSearchParams } from "react-router-dom";
import { Link, useNavigate } from "../app/navigation.js";
import { Avatar } from "../art/Avatar.js";
import { AVATAR_ART } from "../art/avatarManifest.js";
import { useActivity } from "../app/ActivityController.js";
import { usePlayerState } from "../app/ChannelProvider.js";
import { mobileCopy } from "../app/mobileCopy.js";
import { useUiPreferences } from "../app/UiPreferences.js";
import { ClaimAccount } from "./ClaimAccount.js";
import { SecuritySettings } from "./SecuritySettings.js";
import { useLocale, type Locale } from "../i18n.js";
import {
  api,
  ApiError,
  type CoinTier,
  type DailyView,
  type Leaderboard,
  type MatchMode,
  type MatchOptions,
  type PlayerStats,
  type RulesetSummary,
} from "../net/api.js";
import { refreshSession, useSession } from "../net/session.js";
import { useProfileEditor } from "../net/profile.js";
import {
  BottomSheet,
  Button,
  Coins,
  EmptyState,
  ErrorNote,
  PageHeader,
  Panel,
  SegmentedControl,
  Skeleton,
  StateBanner,
} from "../ui/primitives.js";
import {
  GameOptionCard,
  GameSectionHeader,
  MatchConfigurationSummary,
} from "../ui/GameFlow.js";
import { Replays } from "./Replays.js";
import { AudioSettingsPanel } from "../ui/AudioSettings.js";
import "./mobile-screens.css";

const seconds = (milliseconds: number) =>
  `${Math.max(0, Math.floor(milliseconds / 1000))}s`;
const AVATARS = Object.keys(AVATAR_ART).map((id) => ({ id }));
const AudioSettings = AudioSettingsPanel;

/** Weekly mission shape, mirrored from the progress payload. */
const WEEKLY_TARGET = 8;
const WEEKLY_MILESTONES = [3, 8] as const;

/**
 * Coarse "time until" for the weekly reset. Day resolution above 24h, hour
 * resolution below it — a reset three days out does not need minutes, and one
 * two hours out does not want to read "0 days".
 */
function untilLabel(locale: Locale, milliseconds: number): string {
  const hours = Math.max(0, Math.round(milliseconds / 3_600_000));
  if (hours >= 24) {
    const days = Math.round(hours / 24);
    return locale === "zh-CN" ? `${days} 天` : `${days}d`;
  }
  return locale === "zh-CN" ? `${hours} 小时` : `${hours}h`;
}

/**
 * `navigator.onLine` is a snapshot, not a subscription. Reading it during render
 * — as this screen used to — means the offline banner reflects whatever was true
 * at mount and never changes again.
 */
/**
 * Today's deal.
 *
 * Everyone is dealt the same hand and gets one go at it, so the card has three
 * states worth telling apart: not started, in play, and finished — where the
 * result is the interesting part and the ladder is what it was for.
 */
function DailyDealCard({ c }: {
  c: (key: Parameters<typeof mobileCopy>[1], params?: Record<string, string | number>) => string;
}) {
  const navigate = useNavigate();
  const [view, setView] = useState<DailyView | null>(null);
  const [failed, setFailed] = useState(false);
  const [attemptKey, setAttemptKey] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    setFailed(false);
    void api.daily().then((next) => { if (live) setView(next); }).catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [attemptKey]);

  async function play() {
    setBusy(true);
    try {
      navigate(`/table/${(await api.playDaily()).roomId}`);
    } catch {
      // "Already played today" is the refusal worth showing, and reloading the
      // card is what says it.
      setFailed(true);
      void api.daily().then(setView).catch(() => undefined);
    } finally { setBusy(false); }
  }

  if (failed) return <Panel><EmptyState title={c("couldNotLoad")} action={<Button onClick={() => setAttemptKey((key) => key + 1)}>{c("retry")}</Button>} /></Panel>;
  if (view === null) return <Panel><Skeleton lines={3} /></Panel>;
  const attempt = view.attempt;
  const done = attempt !== null && attempt.state !== "playing";

  return (
    <Panel className="daily-deal-card">
      <div className="section-heading"><h2>{c("dailyDeal")}</h2></div>
      <p className="section-hint">{done ? c("dailyNext") : c("dailyDealHint")}</p>
      {done && attempt !== null ? (
        <p className="daily-deal-result">
          <strong>{attempt.state === "aborted" ? c("dailyAborted") : attempt.won === true ? c("dailyWon") : c("dailyLost")}</strong>
          {attempt.state !== "aborted" && <span>{c("dailyPlace", { position: attempt.position ?? 4 })}</span>}
          {attempt.rank !== null && attempt.players !== null && (
            <span>{c("dailyRank", { rank: attempt.rank, players: attempt.players })}</span>
          )}
        </p>
      ) : (
        <Button variant="primary" busy={busy} onClick={() => void play()}>
          {attempt === null ? c("dailyPlay") : c("dailyResume")}
        </Button>
      )}
      {view.standings.length > 0 ? (
        <ol className="daily-deal-board">
          {view.standings.slice(0, 5).map((row) => (
            <li key={row.userId}>
              <b className="tabular">{row.rank}</b>
              <Avatar avatarId={row.avatarId} size={24} />
              <span>{row.displayName}</span>
              <small>{c("dailyPlace", { position: row.position })}</small>
            </li>
          ))}
        </ol>
      ) : done && <p className="section-hint">{c("dailyNobody")}</p>}
    </Panel>
  );
}

export function Home({ wide = false }: { wide?: boolean } = {}) {
  const { locale, c, me, activity, loading, navigate, offline, sheet, setSheet, roomTab, setRoomTab, mode, setMode, rules, tiers, rulesetId, setRulesetId, coinTier, setCoinTier, roomCode, setRoomCode, busy, error, setError, now, liveRooms, startMatch, createRoom, createParty, joinRoom, cancelMatch, claimReward } = useLobby();
  const activityAction = (() => {
    if (loading || activity === null) return <Skeleton lines={3} />;
    if (activity.type === "match")
      return (
        <>
          <p className="activity-kicker">{c("currentActivity")}</p>
          <h2>{c("returnTable")}</h2>
          <p>
            {c("matchInProgress")} ·{" "}
            {activity.away ? c("autoPlaying") : c("waitingForYou")}
          </p>
          <Button
            size="lg"
            variant="primary"
            onClick={() => navigate(`/table/${activity.roomId}`)}
          >
            {c("returnTable")}
          </Button>
        </>
      );
    if (activity.type === "queue")
      return (
        <>
          <p className="activity-kicker">{c("matching")}</p>
          <h2 className="activity-time tabular">
            {seconds(now - activity.joinedAt)}
          </h2>
          <div className="action-pair">
            {activity.lobbyCode && <Button variant="secondary" onClick={() => navigate(`/room/${activity.lobbyCode}`)}>{c("continueReady")}</Button>}
            <Button
              variant="ghost"
              busy={busy}
              onClick={() => void cancelMatch()}
            >
              {c("cancelMatch")}
            </Button>
          </div>
        </>
      );
    if (activity.type === "party")
      return (
        <>
          <p className="activity-kicker">{c("currentActivity")}</p>
          <h2>{c("party")}</h2>
          <p>
            {activity.members.length}/2 {c("playersJoined")}
          </p>
          <Button
            variant="primary"
            size="lg"
            onClick={() => navigate(`/party/${activity.partyId}`)}
          >
            {c("continueParty")}
          </Button>
        </>
      );
    if (activity.type === "lobby")
      return (
        <>
          <p className="activity-kicker">{c("currentActivity")}</p>
          <h2>{c("room")}</h2>
          <p>
            {activity.kind === "ready"
              ? c("readyCheck")
              : `${c("roomCodeLabel")} ${activity.code}`}
          </p>
          <Button
            variant="primary"
            size="lg"
            onClick={() => navigate(`/room/${activity.code}`)}
          >
            {c("continueReady")}
          </Button>
        </>
      );
    return (
      <>
        <div className="lobby-intro">
          <span className="activity-kicker"><Sparkles size={16} /> {locale === "zh-CN" ? "好牌局，从这里开始" : "YOUR NEXT GREAT HAND"}</span>
          <h2>{locale === "zh-CN" ? "入座，来一局。" : "Take your seat."}</h2>
          <p>{locale === "zh-CN" ? "与好友并肩，让每一手都有默契。" : "Find your partner. Make every hand count."}</p>
        </div>
        <div className="play-modes">
          <button className="play-mode play-mode-ranked" onClick={() => { setMode("ranked"); setError(null); setSheet("match"); }} disabled={offline || busy}>
            <Trophy className="play-mode-icon" aria-hidden="true" /><span><strong>{c("ranked")}</strong><small>{c("ratingSeason")}</small></span><ArrowUpRight size={20} aria-hidden="true" />
          </button>
          <button className="play-mode play-mode-gold" onClick={() => { if (me?.user.isGuest) { navigate("/me"); return; } setMode("gold"); setError(null); setSheet("match"); }} disabled={offline || busy}>
            <CoinsIcon className="play-mode-icon" aria-hidden="true" /><span><strong>{c("gold")}</strong><small>{me?.user.isGuest ? c("guestKeepTitle") : c("goldReward")}</small></span><ArrowUpRight size={20} aria-hidden="true" />
          </button>
          <button className="play-mode play-mode-room" onClick={() => { setError(null); setSheet("room"); }} disabled={offline || busy}>
            <DoorOpen className="play-mode-icon" aria-hidden="true" /><span><strong>{c("privateRoom")}</strong><small>{locale === "zh-CN" ? "创建房间 · 输入房间码" : "Create a room · Join with a code"}</small></span><ArrowUpRight size={20} aria-hidden="true" />
          </button>
        </div>
        <Button className="lobby-duo" variant="ghost" busy={busy} disabled={offline} onClick={() => void createParty()}><Users size={20} aria-hidden="true" />{c("duo")}<ChevronRight size={16} aria-hidden="true" /></Button>
      </>
    );
  })();

  const weekly = me?.progress.weeklyMissions;
  const weeklyDone = Math.min(WEEKLY_TARGET, weekly?.validMatches ?? 0);
  // Only the next unclaimed milestone is actionable; listing both repeats what
  // the bar already shows.
  const nextMilestone =
    weekly === undefined
      ? null
      : !weekly.firstMilestoneClaimed && weeklyDone < WEEKLY_MILESTONES[0]
        ? c("weeklyFirst")
        : !weekly.secondMilestoneClaimed
          ? c("weeklySecond")
          : null;
  // The reset time is real data the API already returns; it used to be a
  // hardcoded "Resets Monday at 00:00" that was wrong for most of the week.
  const resetLabel =
    weekly === undefined
      ? ""
      : c("resetsIn", { value: untilLabel(locale, weekly.nextResetAt - now) });

  const View = wide ? WideLobbyView : MobileLobbyView;
  return <View title={c("home")} banner={offline ? <StateBanner tone="warning">{c("offline")}</StateBanner> : null}
    primary={<Panel className="play-card">{activityAction}{error && sheet === null && <ErrorNote>{error}</ErrorNote>}</Panel>}
    support={me && (
        <div className="home-support-stack">
          <DailyDealCard c={c} />

          {/* Rewards. Daily claim and weekly progress are the same idea, so
              they share one surface instead of stacking two cards. */}
          {/* A guest earns no coins, so the rewards card would be a daily
              button that refuses and a weekly bar that never pays. The offer
              to keep the account takes its place. */}
          {me.user.isGuest ? (
            <Panel className="rewards-card">
              <div className="section-heading"><h2>{c("guestKeepTitle")}</h2></div>
              <p className="section-hint">{c("guestKeepHint")}</p>
              <Link className="home-daily-reward" to="/me">
                <Mail size={20} aria-hidden="true" />
                <strong>{c("guestKeepCta")}</strong>
              </Link>
            </Panel>
          ) : (
            <Panel className="rewards-card">
              {!me.dailyClaimed && (
                <button
                  className="home-daily-reward"
                  type="button"
                  disabled={busy}
                  aria-busy={busy}
                  onClick={() => void claimReward()}
                >
                  <Gift size={20} aria-hidden="true" />
                  <strong>{c("daily")}</strong>
                  <b>{c("claim")}</b>
                </button>
              )}
              <div className="weekly-row">
                <div className="section-heading">
                  <h2>{c("weeklyMission")}</h2>
                  <span className="weekly-count tabular">
                    {weeklyDone} / {WEEKLY_TARGET}
                  </span>
                </div>
                <div
                  className="weekly-progress-track"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={WEEKLY_TARGET}
                  aria-valuenow={weeklyDone}
                  aria-label={c("weeklyMission")}
                >
                  <i style={{ width: `${(weeklyDone / WEEKLY_TARGET) * 100}%` }} />
                  {/* Milestones as ticks on the bar rather than a second row of
                      labels repeating what the bar already shows. */}
                  {WEEKLY_MILESTONES.map((at) => (
                    <u
                      key={at}
                      className={weeklyDone >= at ? "reached" : ""}
                      style={{ left: `${(at / WEEKLY_TARGET) * 100}%` }}
                    />
                  ))}
                </div>
                <small className="weekly-caption">
                  {nextMilestone === null ? c("weeklyAllDone") : nextMilestone}
                  {" · "}
                  {resetLabel}
                </small>
              </div>
            </Panel>
          )}
        </div>
      )}
    live={<section className="active-rooms-section">
        {liveRooms === null ? (
          <Skeleton lines={2} />
        ) : liveRooms.length === 0 ? (
          /* Nothing to show collapses to a single row rather than a heading
             plus a full panel saying "no". */
          <Link className="active-rooms-empty" to="/watch">
            <span aria-hidden="true">♣</span>
            <p>{c("noActiveRooms")}</p>
            <b>{c("watchAll")}</b>
          </Link>
        ) : (
          <>
            <div className="section-heading">
              <h2>{c("activeRooms")}</h2>
              <Link to="/watch">{c("watchAll")}</Link>
            </div>
            <div className="active-room-list">
              {liveRooms.slice(0, 2).map((table) => (
                <Link
                  className="active-room-card"
                  to={`/watch/${table.roomId}`}
                  key={table.roomId}
                >
                  <span className="live-dot" aria-hidden="true" />
                  <span className="active-room-info">
                    <b>{table.mode === "ranked" ? c("ranked") : c("gold")}</b>
                    <small>
                      {table.rulesetId === "standard"
                        ? c("rulesStandard")
                        : table.rulesetId}
                      {table.mode === "gold" ? ` · ${table.coinTier}` : ""}
                    </small>
                  </span>
                  <strong>{c("watch")}</strong>
                </Link>
              ))}
            </div>
          </>
        )}
      </section>}
    dialogs={<><BottomSheet
        open={sheet === "match"}
        title={c("setup")}
        onClose={() => setSheet(null)}
        size={wide ? "wide" : "default"}
        className={wide ? "wide-game-dialog" : undefined}
        footer={!wide ? (
          <Button
            variant="primary"
            size="lg"
            busy={busy}
            onClick={() => void startMatch()}
          >
            {c("confirmMatch")}
          </Button>
        ) : undefined}
      >
        {wide ? (
          <WideGameSetup
            kind="match"
            mode={mode}
            setMode={setMode}
            rules={rules}
            rulesetId={rulesetId}
            setRulesetId={setRulesetId}
            tiers={tiers}
            coinTier={coinTier}
            setCoinTier={setCoinTier}
            availableTierIds={new Set(me?.availableTiers.map((tier) => tier.id))}
            roomTab={roomTab}
            setRoomTab={setRoomTab}
            roomCode={roomCode}
            setRoomCode={setRoomCode}
            busy={busy}
            error={error}
            onConfirm={() => void startMatch()}
            onJoin={() => void joinRoom()}
          />
        ) : (
          <>
            <GameSetup
              mode={mode}
              setMode={setMode}
              rules={rules}
              rulesetId={rulesetId}
              setRulesetId={setRulesetId}
              tiers={tiers}
              coinTier={coinTier}
              setCoinTier={setCoinTier}
              availableTierIds={new Set(me?.availableTiers.map((tier) => tier.id))}
            />
            <StateBanner>{c("autoFill")}</StateBanner>
            {error && <ErrorNote>{error}</ErrorNote>}
          </>
        )}
      </BottomSheet>
      <BottomSheet
        open={sheet === "room"}
        title={c("privateRoom")}
        onClose={() => setSheet(null)}
        size={wide ? "wide" : "default"}
        className={wide ? "wide-game-dialog" : undefined}
        footer={!wide && roomTab === "create" ? (
          <Button
            variant="primary"
            size="lg"
            busy={busy}
            onClick={() => void createRoom()}
          >
            {c("create")}
          </Button>
        ) : undefined}
      >
        {wide ? (
          <WideGameSetup
            kind="room"
            mode="gold"
            setMode={() => {}}
            rules={rules}
            rulesetId={rulesetId}
            setRulesetId={setRulesetId}
            tiers={tiers}
            coinTier={coinTier}
            setCoinTier={setCoinTier}
            availableTierIds={new Set(me?.availableTiers.map((tier) => tier.id))}
            roomTab={roomTab}
            setRoomTab={setRoomTab}
            roomCode={roomCode}
            setRoomCode={setRoomCode}
            busy={busy}
            error={error}
            onConfirm={() => void createRoom()}
            onJoin={() => void joinRoom()}
          />
        ) : (
          <>
            <SegmentedControl label={c("privateRoom")} value={roomTab} onChange={setRoomTab} options={[{ value: "create", label: c("create") }, { value: "join", label: c("enterCode") }]} />
            {roomTab === "create" ? <>
            <GameSetup
              mode="gold"
              setMode={() => {}}
              rules={rules}
              rulesetId={rulesetId}
              setRulesetId={setRulesetId}
              tiers={tiers}
              coinTier={coinTier}
              setCoinTier={setCoinTier}
              availableTierIds={new Set(me?.availableTiers.map((tier) => tier.id))}
              fixedGold
            />
            <StateBanner>{c("privateRoomHint")}</StateBanner>
            </> : <div className="join-code">
              <input
                value={roomCode}
                onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
                aria-label={c("roomCodeLabel")}
                placeholder={c("roomCodePlaceholder")}
                maxLength={8}
              />
              <Button busy={busy} onClick={() => void joinRoom()}>
                {c("enterCode")}
              </Button>
            </div>}
            {error && <ErrorNote>{error}</ErrorNote>}
          </>
        )}
      </BottomSheet></>}
  />;
}

function GameSetup({
  mode,
  setMode,
  rules,
  rulesetId,
  setRulesetId,
  tiers,
  coinTier,
  setCoinTier,
  availableTierIds,
  fixedGold = false,
}: {
  mode: MatchMode;
  setMode: (v: MatchMode) => void;
  rules: RulesetSummary[];
  rulesetId: string;
  setRulesetId: (v: string) => void;
  tiers: CoinTier[];
  coinTier: string;
  setCoinTier: (v: string) => void;
  availableTierIds: ReadonlySet<string>;
  fixedGold?: boolean;
}) {
  const { locale } = useLocale();
  const c = (
    key: Parameters<typeof mobileCopy>[1],
    params?: Record<string, string | number>,
  ) => mobileCopy(locale, key, params);
  return (
    <div className="setup-form">
      {!fixedGold && (
        <label>
          <span>{c("modeLabel")}</span>
          <div className="choice-grid">
            <button
              type="button"
              className={mode === "ranked" ? "selected" : ""}
              onClick={() => setMode("ranked")}
            >
              {c("ranked")}
              <small>{c("ratingSeason")}</small>
            </button>
            <button
              type="button"
              className={mode === "gold" ? "selected" : ""}
              onClick={() => setMode("gold")}
            >
              {c("gold")}
              <small>{c("goldReward")}</small>
            </button>
          </div>
        </label>
      )}
      <label>
        <span>{c("rulesLabel")}</span>
        <div className="filter-chips">
          {rules.map((rule) => (
            <button
              type="button"
              key={rule.id}
              className={rulesetId === rule.id ? "on" : ""}
              onClick={() => setRulesetId(rule.id)}
            >
              {rule.label}
            </button>
          ))}
        </div>
      </label>
      {(mode === "gold" || fixedGold) && (
        <label>
          <span>{c("goldTier")}</span>
          <div className="filter-chips gold-tier-options">
            {tiers.map((tier) => {
              const available = availableTierIds.has(tier.id);
              const requirement = c("requiredLevelBalance", {
                level: tier.requiredLevel,
                coins: tier.requiredBalance,
              });
              return (
                <button
                  type="button"
                  key={tier.id}
                  className={`${coinTier === tier.id ? "on" : ""} ${available ? "" : "locked"}`}
                  onClick={() => setCoinTier(tier.id)}
                  disabled={!available}
                  title={available ? undefined : requirement}
                >
                  <span>{tier.label}</span>
                  <small>
                    {c("winNet", { coins: tier.entryCoins })} · {c("lossNet", { coins: tier.entryCoins })}
                  </small>
                  {!available && (
                    <small>
                      {c("locked")} · {requirement}
                    </small>
                  )}
                </button>
              );
            })}
          </div>
        </label>
      )}
    </div>
  );
}

function WideGameSetup({
  kind,
  mode,
  setMode,
  rules,
  rulesetId,
  setRulesetId,
  tiers,
  coinTier,
  setCoinTier,
  availableTierIds,
  roomTab,
  setRoomTab,
  roomCode,
  setRoomCode,
  busy,
  error,
  onConfirm,
  onJoin,
}: {
  kind: "match" | "room";
  mode: MatchMode;
  setMode: (value: MatchMode) => void;
  rules: RulesetSummary[];
  rulesetId: string;
  setRulesetId: (value: string) => void;
  tiers: CoinTier[];
  coinTier: string;
  setCoinTier: (value: string) => void;
  availableTierIds: ReadonlySet<string>;
  roomTab: "create" | "join";
  setRoomTab: (value: "create" | "join") => void;
  roomCode: string;
  setRoomCode: (value: string) => void;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onJoin: () => void;
}) {
  const { locale } = useLocale();
  const c = (key: Parameters<typeof mobileCopy>[1], params?: Record<string, string | number>) => mobileCopy(locale, key, params);
  const selectedRule = rules.find((rule) => rule.id === rulesetId) ?? rules[0];
  const selectedTier = tiers.find((tier) => tier.id === coinTier) ?? tiers[0];
  const gold = kind === "room" || mode === "gold";

  if (kind === "room" && roomTab === "join") {
    return (
      <div className="wide-game-setup wide-game-setup-join">
        <div className="wide-room-tabs" role="tablist" aria-label={c("privateRoom")}>
          <button type="button" role="tab" aria-selected="false" onClick={() => setRoomTab("create")}>{c("createRoomTab")}</button>
          <button type="button" role="tab" aria-selected="true" className="active" onClick={() => setRoomTab("join")}>{c("joinRoomTab")}</button>
        </div>
        <Panel tone="quiet" className="wide-join-room">
          <GameSectionHeader suit="♣" title={c("joinRoomTab")} hint={c("joinRoomHint")} />
          <label className="field">
            <span>{c("roomCodeLabel")}</span>
            <input className="field-input tabular" value={roomCode} onChange={(event) => setRoomCode(event.target.value.toUpperCase())} placeholder={c("roomCodePlaceholder")} maxLength={8} autoFocus />
          </label>
          <Button variant="primary" size="lg" busy={busy} disabled={roomCode.trim().length < 4} onClick={onJoin}>{c("enterCode")}</Button>
          {error && <ErrorNote>{error}</ErrorNote>}
        </Panel>
      </div>
    );
  }

  const rows = [
    { label: c("modeLabel"), value: gold ? c("gold") : c("ranked") },
    { label: c("rulesLabel"), value: selectedRule?.label ?? "—" },
    { label: c("goldTier"), value: gold && selectedTier ? <Coins amount={selectedTier.entryCoins} /> : c("freeEntry") },
    ...(gold && selectedTier ? [{ label: c("potentialResult"), value: `${c("winNet", { coins: selectedTier.entryCoins })} · ${c("lossNet", { coins: selectedTier.entryCoins })}` }] : []),
    ...(kind === "room" ? [{ label: c("roomVisibility"), value: c("privateAccess") }] : []),
  ];

  return (
    <div className="wide-game-setup">
      {kind === "room" && (
        <div className="wide-room-tabs" role="tablist" aria-label={c("privateRoom")}>
          <button type="button" role="tab" aria-selected="true" className="active" onClick={() => setRoomTab("create")}>{c("createRoomTab")}</button>
          <button type="button" role="tab" aria-selected="false" onClick={() => setRoomTab("join")}>{c("joinRoomTab")}</button>
        </div>
      )}
      <div className="wide-game-options">
        {kind === "match" && (
          <section>
            <GameSectionHeader suit="♠" title={c("modeLabel")} />
            <div className="game-option-grid">
              <GameOptionCard title={c("ranked")} meta={c("ratingSeason")} badge={c("freeEntry")} selected={mode === "ranked"} onClick={() => setMode("ranked")} />
              <GameOptionCard title={c("gold")} meta={c("goldReward")} selected={mode === "gold"} onClick={() => setMode("gold")} />
            </div>
          </section>
        )}
        <section>
          <GameSectionHeader suit="♦" title={c("rulesLabel")} />
          <div className="game-option-grid">
            {rules.map((rule) => <GameOptionCard key={rule.id} title={rule.label} selected={rulesetId === rule.id} onClick={() => setRulesetId(rule.id)} />)}
          </div>
        </section>
        {gold && (
          <section>
            <GameSectionHeader suit="♥" title={c("goldTier")} />
            <div className="game-option-grid">
              {tiers.map((tier) => {
                const available = availableTierIds.has(tier.id);
                return <GameOptionCard key={tier.id} title={tier.label} meta={<Coins amount={tier.entryCoins} />} badge={available ? undefined : c("locked")} selected={coinTier === tier.id} disabled={!available} onClick={() => setCoinTier(tier.id)} />;
              })}
            </div>
          </section>
        )}
      </div>
      <MatchConfigurationSummary
        eyebrow={c("tableSummary")}
        title={kind === "room" ? c("privateRoom") : c("setup")}
        rows={rows}
        status={<StateBanner>{kind === "room" ? c("privateRoomHint") : c("autoFill")}</StateBanner>}
        actions={<><Button variant="primary" size="lg" busy={busy} onClick={onConfirm}>{kind === "room" ? c("create") : c("confirmMatch")}</Button>{error && <ErrorNote>{error}</ErrorNote>}</>}
      />
    </div>
  );
}

export function RecordScreen() {
  const { locale } = useLocale();
  const c = mobileCopy.bind(null, locale);
  const session = useSession();
  const progress = session.status === "signedIn" ? session.me.progress : null;
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "matches" ? "matches" : "overview";
  const [stats, setStats] = useState<PlayerStats | null>(null);
  const [board, setBoard] = useState<Leaderboard | null>(null);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    setFailed(false);
    if (tab === "overview")
      void Promise.all([api.stats(), api.leaderboard(50)])
        .then(([s, b]) => {
          setStats(s);
          setBoard(b);
        })
        .catch(() => setFailed(true));
  }, [tab, retry]);
  return (
    <div className="screen-stack">
      <h1 className="screen-title">{c("record")}</h1>
      <SegmentedControl
        label={c("record")}
        value={tab}
        options={
          [
            { value: "overview", label: c("overview") },
            { value: "matches", label: c("matches") },
          ] as const
        }
        onChange={(value) => setParams({ tab: value })}
      />
      {tab === "matches" ? (
        <Replays />
      ) : failed ? <EmptyState title={c("couldNotLoad")} action={<Button onClick={() => setRetry((value) => value + 1)}>{c("retry")}</Button>} /> : stats && board ? (
        <>
          <Panel className="season-card">
            <div className="season-topline">
              <span>
                <small>{board.season.label}</small>
                <h2>#{board.me?.rank ?? "—"}</h2>
              </span>
              <strong className="tabular">
                {board.me?.rating ?? stats.competition.currentRating}
              </strong>
            </div>
            <div className="record-strip">
              <span>
                <b>{Math.round(stats.overview.winRate * 100)}%</b>
                <small>{c("winRate")}</small>
              </span>
              <span>
                <b>{stats.overview.matches}</b>
                <small>{c("totalMatches")}</small>
              </span>
              <span>
                <b>{stats.play.bombs}</b>
                <small>{c("bombCount")}</small>
              </span>
            </div>
            <p>
              {progress && progress.ratedMatches < 10
                ? c("placementStatus", { count: progress.ratedMatches })
                : c("establishedRating")}
              {" · "}
              {c("seasonResetNote")}
            </p>
          </Panel>
          {stats.competition.trend.length > 0 && (
            <section className="compact-section">
              <h2>{c("recentTrend")}</h2>
              <Panel>
                <div className="trend-bars">
                  {stats.competition.trend.slice(-8).map((point, index) => (
                    <i
                      key={index}
                      className={point.won ? "up" : "down"}
                      style={{
                        height: `${20 + Math.min(44, Math.abs(point.ratingDelta) * 3)}px`,
                      }}
                    />
                  ))}
                </div>
              </Panel>
            </section>
          )}
          <section>
            <h2>{c("seasonBoard")}</h2>
            <Panel>
              <div className="leader-head">
                <span>{c("rankPlayer")}</span>
                <span>{c("winsTotalRating")}</span>
              </div>
              {board.top.slice(0, 10).map((entry) => (
                <Link
                  className="leader-row"
                  to={`/player/${entry.userId}`}
                  key={entry.userId}
                >
                  <b>#{entry.rank}</b>
                  <Avatar avatarId={entry.avatarId} size={36} />
                  <span>{entry.name}</span>
                  <small>
                    {entry.wins}/{entry.matches}　{entry.rating}
                  </small>
                </Link>
              ))}
            </Panel>
          </section>
        </>
      ) : (
        <Skeleton lines={7} />
      )}
    </div>
  );
}
export function SocialScreen() {
  const { locale } = useLocale();
  return <div className="screen-stack mobile-social-screen"><h1 className="screen-title">{mobileCopy(locale, "social")}</h1><Friends /></div>;
}

function profileErrorText(error: unknown, c: (key: Parameters<typeof mobileCopy>[1]) => string): string {
  if (error instanceof ApiError && error.code === "invalid_profile") return c("profileInvalid");
  return c("profileSaveError");
}

export function MeScreen({ wide = false }: { wide?: boolean } = {}) {
  const { locale, setLocale } = useLocale();
  const c = (key: Parameters<typeof mobileCopy>[1]) => mobileCopy(locale, key);
  const session = useSession();
  const me = session.status === "signedIn" ? session.me : null;
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "settings" ? "settings" : "profile";
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [audioOpen, setAudioOpen] = useState(false);
  const [securityOpen, setSecurityOpen] = useState(false);
  const { preferences, setPreference } = useUiPreferences();
  const profileEditor = useProfileEditor(me?.user ?? null);
  const [playerCode, setPlayerCode] = useState<string | null>(null);
  useEffect(() => { void api.socialMe().then((value) => setPlayerCode(value.playerCode)).catch(() => undefined); }, [me?.user.id]);
  if (!me) return null;
  return (
    <div className={`screen-stack me-screen${wide ? " me-screen-wide" : ""}`} data-view={tab}>
      {wide ? <PageHeader title={c("me")} /> : <h1 className="screen-title">{c("me")}</h1>}
      <SegmentedControl
        label={c("me")}
        value={tab}
        options={
          [
            { value: "profile", label: c("profile") },
            { value: "settings", label: c("settings") },
          ] as const
        }
        onChange={(value) => setParams({ tab: value })}
      />
      {tab === "profile" ? (
        <>
          {me.user.isGuest && <ClaimAccount />}
          <Panel className="profile-hero">
            <button
              className="avatar-edit"
              type="button"
              onClick={() => setAvatarOpen(true)}
            >
              <Avatar avatarId={profileEditor.draft.avatarId} size={64} />
              <span>{c("changeAvatar")}</span>
            </button>
            <div>
              <h2>{me.user.displayName}</h2>
              <p>
                {mobileCopy(locale, "levelRating", {
                  level: me.progress.level,
                  rating: me.progress.rating,
                })}
              </p>
            </div>
          </Panel>
          <label className="field">
            <span>{c("nickname")}</span>
            <input
              value={profileEditor.draft.displayName}
              maxLength={24}
              onChange={(e) => profileEditor.setDisplayName(e.target.value)}
            />
          </label>
          {!me.user.isGuest && <Panel className="profile-id-row">
            <span>
              <small>{c("playerId")}</small>
              <b className="tabular">{playerCode ?? "—"}</b>
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                void navigator.clipboard.writeText(playerCode ?? "").catch(() => undefined)
              }
            >
              <Clipboard size={15} aria-hidden="true" />
              {c("copy")}
            </Button>
          </Panel>}
          <div className="sticky-action">
            <Button
              variant="primary"
              busy={profileEditor.saving}
              disabled={
                profileEditor.draft.displayName.trim().length < 2 ||
                !profileEditor.dirty
              }
              onClick={() => void profileEditor.save()}
            >
              {c("save")}
            </Button>
            {profileEditor.error !== null && <ErrorNote>{profileErrorText(profileEditor.error, c)}</ErrorNote>}
            {profileEditor.status === "saved" && <p className="success-note" role="status">{c("profileSaved")}</p>}
          </div>
        </>
      ) : (
        <>
          <section>
            <h2>{c("preferences")}</h2>
            <Panel className="settings-list">
              <button
                type="button"
                onClick={() => {
                  const next = locale === "zh-CN" ? "en" : "zh-CN";
                  setLocale(next);
                  void api.updateProfile({ locale: next });
                }}
              >
                <span><Globe2 aria-hidden="true" />{c("language")}</span>
                <b>{locale === "zh-CN" ? "中文" : "English"}</b>
              </button>
              <button type="button" onClick={() => setAudioOpen(true)}>
                <span><Volume2 aria-hidden="true" />{c("sound")}</span>
                <ChevronRight aria-hidden="true" />
              </button>
            </Panel>
          </section>
          <section>
            <h2>{c("accessibility")}</h2>
            <Panel className="settings-list">
              <label>
                <span><Accessibility aria-hidden="true" />{c("reduceMotion")}</span>
                <input type="checkbox" checked={preferences.reducedMotion} onChange={(event) => setPreference("reducedMotion", event.target.checked)} />
              </label>
              <label>
                <span><Accessibility aria-hidden="true" />{c("coaching")}</span>
                <input type="checkbox" checked={preferences.coaching} onChange={(event) => setPreference("coaching", event.target.checked)} />
              </label>
            </Panel>
          </section>
          <section>
            <h2>{c("account")}</h2>
            <Panel className="settings-list">
              <Link to="/rules" state={{ from: "/me?tab=settings" }}>
                <span>{c("ruleCenter")}</span>
                <ChevronRight aria-hidden="true" />
              </Link>
              {!me.user.isGuest && <button type="button" onClick={() => setSecurityOpen(true)}>
                <span><ShieldCheck aria-hidden="true" />{c("security")}</span>
                <ChevronRight aria-hidden="true" />
              </button>}
            </Panel>
          </section>
          <Button
            className="signout"
            variant="danger"
            onClick={() => void api.logout().then(refreshSession).catch(refreshSession)}
          >
            {c("signOut")}
          </Button>
        </>
      )}
      <BottomSheet
        open={avatarOpen}
        title={c("chooseAvatar")}
        onClose={() => setAvatarOpen(false)}
      >
        <div className="avatar-sheet">
          {AVATARS.map((item) => (
            <button
              type="button"
              className={profileEditor.draft.avatarId === item.id ? "selected" : ""}
              key={item.id}
              onClick={() => {
                profileEditor.setAvatarId(item.id);
                setAvatarOpen(false);
              }}
            >
              <Avatar avatarId={item.id} size={52} />
            </button>
          ))}
        </div>
      </BottomSheet>
      <BottomSheet
        open={audioOpen}
        title={c("sound")}
        onClose={() => setAudioOpen(false)}
      >
        <AudioSettings />
      </BottomSheet>
      {!me.user.isGuest && (
        <BottomSheet
          open={securityOpen}
          title={c("security")}
          onClose={() => setSecurityOpen(false)}
          size={wide ? "wide" : "default"}
          className={wide ? "wide-game-dialog" : undefined}
        >
          <SecuritySettings hasPassword={me.user.hasPassword} />
        </BottomSheet>
      )}
    </div>
  );
}

export function RulesScreen() {
  const { locale } = useLocale();
  const c = (key: Parameters<typeof mobileCopy>[1]) => mobileCopy(locale, key);
  const location = useLocation();
  const source = (location.state as { from?: string } | null)?.from;
  return (
    <div className="screen-stack detail-screen guide-page">
      <PageHeader
        title={c("guideTitle")}
        backTo={source ?? "/me?tab=settings"}
      />

      <section className="guide-hero" aria-labelledby="guide-goal">
        <div className="guide-hero-copy">
          <span>{c("guideMinutes")}</span>
          <h2 id="guide-goal">{c("guideHeroTitle")}</h2>
          <p>{c("guideHeroBody")}</p>
        </div>
        <div className="guide-seats" aria-label={c("guideSeatsLabel")}>
          <div className="guide-seat guide-seat-top">
            <Avatar avatarId="avatar-05" size={42} title={c("guidePartner")} />
            <small>{c("guidePartner")}</small>
          </div>
          <div className="guide-seat guide-seat-left">
            <Avatar avatarId="avatar-09" size={38} title={c("guideRival")} />
          </div>
          <div className="guide-seat guide-seat-right">
            <Avatar avatarId="avatar-13" size={38} title={c("guideRival")} />
          </div>
          <div className="guide-seat guide-seat-bottom">
            <Avatar avatarId="avatar-01" size={46} title={c("guideYou")} />
            <small>{c("guideYou")}</small>
          </div>
          <span className="partner-line" aria-hidden="true" />
          <b>{c("guidePartners")}</b>
        </div>
      </section>

      <section className="guide-section" aria-labelledby="guide-flow-title">
        <h2 id="guide-flow-title">{c("guideThreeThings")}</h2>
        <ol className="guide-flow">
          <li>
            <span className="guide-step-number">1</span>
            <div
              className="guide-step-visual guide-avatar-pair"
              aria-hidden="true"
            >
              <Avatar avatarId="avatar-01" size={34} />
              <Avatar avatarId="avatar-05" size={34} />
            </div>
            <span>
              <b>{c("guideTeamTitle")}</b>
              <small>{c("guideTeamBody")}</small>
            </span>
          </li>
          <li>
            <span className="guide-step-number">2</span>
            <div
              className="guide-step-visual guide-card-fan"
              aria-hidden="true"
            >
              <MiniCard rank="7" suit="♠" />
              <MiniCard rank="9" suit="♥" red />
              <MiniCard rank="K" suit="♣" />
            </div>
            <span>
              <b>{c("guideFinishTitle")}</b>
              <small>{c("guideFinishBody")}</small>
            </span>
          </li>
          <li>
            <span className="guide-step-number">3</span>
            <div
              className="guide-step-visual guide-level-jump"
              aria-hidden="true"
            >
              <i>2</i>
              <em>→</em>
              <i>5</i>
            </div>
            <span>
              <b>{c("guideUpgradeActionTitle")}</b>
              <small>{c("guideUpgradeActionBody")}</small>
            </span>
          </li>
        </ol>
      </section>

      <section className="guide-section" aria-labelledby="guide-cards-title">
        <h2 id="guide-cards-title">{c("guideCardsTitle")}</h2>
        <div className="guide-key-cards">
          <Panel className="guide-key-card">
            <div className="guide-card-stage" aria-hidden="true">
              <MiniCard rank="7" suit="♠" badge="●" />
            </div>
            <span>
              <b>{c("guideLevelTitle")}</b>
              <small>{c("guideLevelBody")}</small>
            </span>
          </Panel>
          <Panel className="guide-key-card guide-wild-card">
            <div className="guide-card-stage" aria-hidden="true">
              <MiniCard rank="7" suit="♥" red badge="✦" />
            </div>
            <span>
              <b>{c("guideWildTitle")}</b>
              <small>{c("guideWildBody")}</small>
            </span>
          </Panel>
          <Panel className="guide-key-card guide-bomb-card">
            <div className="guide-bomb" aria-hidden="true">
              {["♠", "♥", "♣", "♦"].map((suit, index) => (
                <MiniCard
                  key={suit}
                  rank="8"
                  suit={suit}
                  red={index % 2 === 1}
                />
              ))}
            </div>
            <span>
              <b>{c("guideBombTitle")}</b>
              <small>{c("guideBombBody")}</small>
            </span>
          </Panel>
        </div>
      </section>

      <section className="guide-section" aria-labelledby="guide-upgrade-title">
        <h2 id="guide-upgrade-title">{c("guideUpgradePath")}</h2>
        <Panel className="guide-upgrade-card">
          <div>
            <span>🥇🥈</span>
            <b>{c("guideDoubleWin")}</b>
            <strong>+3</strong>
          </div>
          <div>
            <span>🥇🥈</span>
            <b>{c("guideOneTwo")}</b>
            <strong>+2</strong>
          </div>
          <div>
            <span>🥇🥉</span>
            <b>{c("guideOneThree")}</b>
            <strong>+1</strong>
          </div>
          <footer>
            <b>A</b>
            <span>{c("guideWin")}</span>
          </footer>
        </Panel>
      </section>

      <details className="guide-details">
        <summary>{c("guideMore")}</summary>
        <div className="guide-final-grid">
          <Panel className="guide-tribute-card">
            <div className="tribute-visual" aria-hidden="true">
              <Avatar avatarId="avatar-09" size={34} /><span>↔</span><MiniCard rank="A" suit="♦" red /><span>↔</span><Avatar avatarId="avatar-01" size={34} />
            </div>
            <h3>{c("guideTributeTitle")}</h3><p>{c("guideTributeBody")}</p>
          </Panel>
          <Panel className="guide-modes-card">
            <h3>{c("ruleVariantsTitle")}</h3>
            <div><b>{c("guideCasual")}</b><small>{c("guideCasualBody")}</small></div>
            <div><b>{c("guideStandard")}</b><small>{c("guideStandardBody")}</small></div>
            <div><b>{c("guideCompetitive")}</b><small>{c("guideCompetitiveBody")}</small></div>
          </Panel>
        </div>
      </details>

      <Link className="btn btn-primary btn-md btn-link guide-cta" to="/">
        {c("guideStart")}
      </Link>
    </div>
  );
}

function MiniCard({
  rank,
  suit,
  red = false,
  badge,
}: {
  rank: string;
  suit: string;
  red?: boolean;
  badge?: string;
}) {
  return (
    <span className={`mini-playing-card${red ? " red" : ""}`}>
      <b>{rank}</b>
      <i>{suit}</i>
      {badge && <em>{badge}</em>}
    </span>
  );
}

export function WatchScreen() {
  const { locale } = useLocale();
  const c = (key: Parameters<typeof mobileCopy>[1]) => mobileCopy(locale, key);
  return (
    <div className="screen-stack detail-screen">
      <PageHeader title={c("watch")} backTo="/" />
      <EmptyState
        title={c("noLive")}
        hint={c("noLiveHint")}
        action={<Button onClick={() => location.reload()}>{c("retry")}</Button>}
      />
    </div>
  );
}

export function PartyScreen() {
  const { locale } = useLocale();
  const c = (key: Parameters<typeof mobileCopy>[1]) => mobileCopy(locale, key);
  const { pathname } = useLocation();
  const id = pathname.split("/").at(-1) ?? "";
  const nav = useNavigate();
  const { refresh } = useActivity();
  const [party, setParty] = useState<Awaited<
    ReturnType<typeof api.party>
  > | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const session = useSession();
  const lobbyTick = usePlayerState((state) => state.lobbyTick);
  useEffect(() => {
    let timer = 0;
    // A party that has been disbanded answers 404. Polling it forever produced
    // an unhandled rejection every three seconds behind a skeleton that could
    // never resolve, so a failed poll stops the loop and sends the player home.
    const poll = () =>
      void api.party(id).then(setParty).catch(() => {
        window.clearInterval(timer);
        nav("/", { replace: true });
      });
    poll();
    // Membership changes are published to the party's players, so this is a
    // fallback rather than the mechanism; it used to run every three seconds.
    timer = window.setInterval(poll, 20_000);
    return () => window.clearInterval(timer);
  }, [id, nav, lobbyTick]);
  if (!party) return <Skeleton lines={6} />;
  async function start() {
    if (busy) return;
    setBusy(true);
    setNote(null);
    try {
      await api.joinQueue({
        mode: "ranked",
        rulesetId: "standard",
        partyId: id,
      } as MatchOptions & { partyId: string });
      await refresh();
      nav("/");
    } catch (caught) {
      setNote(gameError(caught, locale));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="screen-stack detail-screen">
      <PageHeader title={c("party")} backTo="/" />
      <Panel className="party-code">
        <small>{c("partyCode")}</small>
        <strong className="tabular">{party.code}</strong>
        <Button onClick={() => void navigator.clipboard.writeText(party.code).then(() => setNote(locale === "zh-CN" ? "房间码已复制" : "Code copied")).catch(() => setNote(locale === "zh-CN" ? "复制失败，请手动复制上方房间码" : "Copy the code above manually"))}>
          {c("share")}
        </Button>
      </Panel>
      <section>
        <h2>{c("teammates")}</h2>
        <div className="party-slots">
          {[0, 1].map((index) => {
            const member = party.members[index];
            return (
              <Panel key={index} className="party-slot">
                {member ? (
                  <>
                    <Avatar
                      avatarId={index === 0 ? "hero-sun" : "hero-mint"}
                      size={56}
                    />
                    <b>{member.name}</b>
                    <small>
                      {member.userId === party.hostUserId
                        ? c("host")
                        : c("joined")}
                    </small>
                  </>
                ) : (
                  <>
                    <span className="empty-avatar">＋</span>
                    <b>{c("waitingTeammate")}</b>
                    <Button onClick={() => nav("/social?tab=friends")}><Users size={20} aria-hidden="true" />{c("inviteFriend")}</Button>
                  </>
                )}
              </Panel>
            );
          })}
        </div>
      </section>
      <StateBanner>{c("partyHint")}</StateBanner>
      {note && <ErrorNote>{note}</ErrorNote>}
      <Button
        variant="primary"
        size="lg"
        busy={busy}
        disabled={party.members.length < 2 || session.status !== "signedIn" || session.me.user.id !== party.hostUserId}
        onClick={() => void start()}
      >
        {c("startPartyMatch")}
      </Button>
      <Button
        variant="danger"
        onClick={() =>
          void api.leaveParty(id)
            .then(() => {
              void refresh();
              nav("/");
            }).catch((caught) => setNote(gameError(caught, locale)))
        }
      >
        {c("leave")}
      </Button>
    </div>
  );
}
